import { combineEpics, StateObservable } from 'redux-observable'
import { EMPTY, Observable, of } from 'rxjs'
import { filter, map, mergeMap, withLatestFrom } from 'rxjs/operators'
import { isNil } from 'lodash-es'
import { CommittingServicingEvent, Deal, DealEvent, DealStatus, EntityType, InitiatingActor, ServicingType } from 'api'
import { GlobalState } from 'store'
import { GridId } from 'store/grid'
import { SelectionActions } from 'store/selection'
import { NotificationsToastActions } from './toastEpic'
import { getIsUpdatingUserFromDifferentOrganisation, ofEntityUpdateType } from './epicUtils'
import { penultimate } from 'utils'
import { SYSTEM_USER_NAME } from 'model'
import { isUserTradingEntityGetterSelector } from 'selectors/adminSelectors'
import { createDealDetailSelector } from 'selectors/dealSelectors'
import { BTQAction } from 'epics'

function dealStatusChangedFilter([deal, oldState]: [Deal, GlobalState]) {
  const oldDeal = oldState.repositoryStore.DEAL[deal.dealId] && oldState.repositoryStore.DEAL[deal.dealId].lastUpdate.entity

  return !oldDeal || deal.status !== oldDeal.status
}

export function dealEventReceivedEpic(
  action$: Observable<BTQAction>,
  state$: StateObservable<GlobalState>,
  _dependencies: {}
): Observable<BTQAction> {
  return action$.pipe(
    ofEntityUpdateType(EntityType.DEAL),
    map(update => update.entity),
    filter(deal => deal.status === DealStatus.DONE),
    withLatestFrom(state$.pipe(penultimate())),
    filter(dealStatusChangedFilter),
    map(([deal, _]) => deal),
    filter(deal => getIsUpdatingUserFromDifferentOrganisation(deal.lastUpdatedBy, state$.value) || deal.lastUpdatedBy === SYSTEM_USER_NAME),
    map(() => SelectionActions.setGridActivityHighlight(GridId.DealsGrid, true, true))
  )
}

function isServicingEventReceiverFilter([deal, oldState]: [Deal, GlobalState], state: GlobalState) {
  const oldDeal = oldState.repositoryStore.DEAL[deal.dealId] && oldState.repositoryStore.DEAL[deal.dealId].lastUpdate.entity

  if (
    !oldDeal ||
    oldDeal.status === deal.status ||
    ((deal.status === DealStatus.DONE || deal.status === DealStatus.CANCELLED) &&
      oldDeal.status !== DealStatus.SERVICING_WITH_INITIATOR &&
      oldDeal.status !== DealStatus.SERVICING_WITH_RESPONDER)
  ) {
    return false
  }

  // If servicing event has started but not sent yet
  if (deal.servicingDetails && !deal.servicingDetails?.isActive) {
    return false
  }

  // If servicing event had been started but cancelled before sending
  if (deal.lastEvent === DealEvent.SERVICING_CANCEL) {
    return false
  }

  return getIsUpdatingUserFromDifferentOrganisation(deal.lastUpdatedBy, state)
}

// Previous Status          -> New Status               | Conditions                                                | Event
// DONE                     -> SERVICING_WITH_RESPONDER |                                                           | START
// SERVICING_WITH_RESP/INIT -> SERVICING_WITH_INIT/RESP |                                                           | SEND
// SERVICING_WITH_RESP/INIT -> DONE                     | replacedWithDealId populated                              | ACCEPT
// SERVICING_WITH_RESP/INIT -> DONE                     | replacedWithDealId not populated, is servicing initiator  | REJECT
// SERVICING_WITH_RESP/INIT -> DONE                     | replacedWithDealId not populated, is servicing responder  | WITHDRAW
export function getServicingEvent(deal: Deal, oldState: GlobalState): CommittingServicingEvent {
  const oldDeal: Deal = oldState.repositoryStore.DEAL[deal.dealId] && oldState.repositoryStore.DEAL[deal.dealId].lastUpdate.entity

  if (oldDeal.status === DealStatus.DONE) {
    switch (deal.servicingDetails?.servicingType?.servicingType) {
      case ServicingType.SUBSTITUTION:
        return DealEvent.SERVICING_START_SUBSTITUTE
      case ServicingType.CLOSE_OPEN:
      case ServicingType.RERATE:
        return DealEvent.SERVICING_START_RERATE
      case ServicingType.TERMINATION:
        return DealEvent.SERVICING_START_TERMINATE
      case ServicingType.RETURN_RECALL:
        return DealEvent.SERVICING_START_RETURN_RECALL
      case ServicingType.ROLL_IN_PLACE:
        return DealEvent.SERVICING_START_ROLL_IN_PLACE
      case ServicingType.SERVICING_TRADER_AMEND:
        return DealEvent.SERVICING_START_TRADER_AMEND
      case ServicingType.SERVICING_TRADER_CANCEL:
        return DealEvent.SERVICING_START_TRADER_CANCEL
      case ServicingType.SERVICING_ADMIN_CANCEL:
        return DealEvent.SERVICING_START_ADMIN_CANCEL
      default:
        throw Error('Servicing details is undefined')
    }
  }

  if (deal.status !== DealStatus.DONE && deal.status !== DealStatus.CANCELLED) {
    return DealEvent.SERVICING_SEND
  }

  if (
    !isNil(deal.replacedWithDealId) ||
    (oldDeal.servicingDetails?.servicingType?.servicingType === ServicingType.TERMINATION && deal.endDate !== null) ||
    (oldDeal.servicingDetails?.servicingType?.servicingType === ServicingType.RERATE &&
      deal.lastEvent !== DealEvent.SERVICING_WITHDRAW &&
      deal.lastEvent !== DealEvent.SERVICING_REJECT) ||
    (oldDeal.servicingDetails?.servicingType?.servicingType === ServicingType.ROLL_IN_PLACE &&
      deal.lastEvent !== DealEvent.SERVICING_WITHDRAW &&
      deal.lastEvent !== DealEvent.SERVICING_REJECT) ||
    (oldDeal.servicingDetails?.servicingType?.servicingType === ServicingType.SERVICING_TRADER_AMEND &&
      deal.lastEvent !== DealEvent.SERVICING_WITHDRAW &&
      deal.lastEvent !== DealEvent.SERVICING_REJECT) ||
    (oldDeal.servicingDetails?.servicingType?.servicingType === ServicingType.SERVICING_TRADER_CANCEL &&
      deal.lastEvent !== DealEvent.SERVICING_WITHDRAW &&
      deal.lastEvent !== DealEvent.SERVICING_REJECT) ||
    (oldDeal.servicingDetails?.servicingType?.servicingType === ServicingType.SERVICING_ADMIN_CANCEL &&
      deal.lastEvent !== DealEvent.SERVICING_WITHDRAW &&
      deal.lastEvent !== DealEvent.SERVICING_REJECT) ||
    (oldDeal.servicingDetails?.servicingType?.servicingType === ServicingType.RETURN_RECALL &&
      deal.lastEvent !== DealEvent.SERVICING_WITHDRAW &&
      deal.lastEvent !== DealEvent.SERVICING_REJECT)
  ) {
    return DealEvent.SERVICING_ACCEPT
  }

  const ownOrder = isUserTradingEntityGetterSelector(oldState)(deal.fundId)
  const isServicingInitiator = ownOrder
    ? oldDeal.servicingDetails?.servicingInitiator === InitiatingActor.ORDER_INITIATOR
    : oldDeal.servicingDetails?.servicingInitiator === InitiatingActor.ORDER_RESPONDER
  return isServicingInitiator ? DealEvent.SERVICING_REJECT : DealEvent.SERVICING_WITHDRAW
}

const mapFilterDealServicingEvent = (action$: Observable<BTQAction>) =>
  action$.pipe(
    ofEntityUpdateType(EntityType.DEAL),
    map(update => update.entity),
    filter(
      deal =>
        deal.status === DealStatus.SERVICING_WITH_INITIATOR ||
        deal.status === DealStatus.SERVICING_WITH_RESPONDER ||
        deal.status === DealStatus.DONE ||
        deal.status === DealStatus.CANCELLED
    )
  )

function doesServicingTypeCreateNewDeal(servicingType: ServicingType | undefined) {
  return servicingType === ServicingType.CLOSE_OPEN || servicingType === ServicingType.TERMINATION
}

export function servicingEventReceivedEpic(
  action$: Observable<BTQAction>,
  state$: StateObservable<GlobalState>,
  _dependencies: {}
): Observable<BTQAction> {
  return action$.pipe(
    mapFilterDealServicingEvent,
    withLatestFrom(state$.pipe(penultimate())),
    filter(dealAndOldState => isServicingEventReceiverFilter(dealAndOldState, state$.value)),
    filter(([deal, oldState]) => {
      if (deal.status === DealStatus.DONE || deal.status === DealStatus.CANCELLED) {
        const lastServicingType =
          oldState.repositoryStore.DEAL[deal.dealId].lastUpdate.entity.servicingDetails?.servicingType?.servicingType
        return lastServicingType !== ServicingType.SERVICING_AMEND
      }

      return (
        deal.servicingDetails?.servicingType?.servicingType !== ServicingType.SERVICING_AMEND &&
        (deal.servicingDetails?.servicingType?.servicingType !== ServicingType.SERVICING_TRADER_AMEND ||
          deal.status === DealStatus.SERVICING_WITH_RESPONDER)
      )
    }),
    mergeMap(([deal, oldState]) => {
      // When a mid trade event terminates, the deal no longer has the servicing details.
      // Therefore using the previous state on a terminating servicing event to get the servicing details.
      const dealDetail = createDealDetailSelector(() => deal.dealId)(
        deal.status === DealStatus.DONE || deal.status === DealStatus.CANCELLED ? oldState : state$.value,
        {}
      )

      if (!dealDetail) {
        return EMPTY
      }

      const servicingEvent = getServicingEvent(deal, oldState)
      if (
        servicingEvent !== DealEvent.SERVICING_ACCEPT ||
        !doesServicingTypeCreateNewDeal(dealDetail.deal.servicingDetails?.servicingType?.servicingType || undefined)
      ) {
        return of(NotificationsToastActions.createDealToast(dealDetail, servicingEvent))
      }

      const updatedDealDetail = createDealDetailSelector(() => deal.dealId)(state$.value, {})
      if (updatedDealDetail) {
        return of(NotificationsToastActions.createDealToast(dealDetail, servicingEvent, updatedDealDetail.deal.replacedWithDealId))
      }

      return of(NotificationsToastActions.createDealToast(dealDetail, servicingEvent))
    })
  )
}

const servicingEventStatuses = [DealStatus.SERVICING_WITH_INITIATOR, DealStatus.SERVICING_WITH_RESPONDER]

export function newServicingEventReceivedEpic(
  action$: Observable<BTQAction>,
  state$: StateObservable<GlobalState>,
  _dependencies: {}
): Observable<BTQAction> {
  return action$.pipe(
    mapFilterDealServicingEvent,
    withLatestFrom(state$.pipe(penultimate())),
    filter(dealStatusChangedFilter),
    map(([deal, _]) => deal),
    filter(deal => servicingEventStatuses.includes(deal.status)),
    filter(deal => deal.servicingDetails?.servicingType?.servicingType !== ServicingType.SERVICING_AMEND),
    filter(deal => !(deal.status === DealStatus.SERVICING_WITH_INITIATOR && !deal.servicingDetails?.isActive)),
    filter(deal => getIsUpdatingUserFromDifferentOrganisation(deal.lastUpdatedBy, state$.value) || deal.lastUpdatedBy === SYSTEM_USER_NAME),
    map(() => SelectionActions.setGridActivityHighlight(GridId.MidTradeEventsGrid, true))
  )
}

export function fundAssignmentEventReceivedEpic(
  action$: Observable<BTQAction>,
  state$: StateObservable<GlobalState>,
  _dependencies: {}
): Observable<BTQAction> {
  return action$.pipe(
    ofEntityUpdateType(EntityType.DEAL),
    map(update => update.entity),
    filter(deal => deal.status === DealStatus.UNASSIGNED),
    withLatestFrom(state$.pipe(penultimate())),
    filter(([deal, oldState]) => {
      const oldDeal = oldState.repositoryStore.DEAL[deal.dealId]?.lastUpdate.entity
      return oldDeal && deal.fundAssignment?.assignmentReminderCount !== oldDeal.fundAssignment?.assignmentReminderCount
    }),
    map(([deal, _]) => deal),
    filter(deal => isUserTradingEntityGetterSelector(state$.value)(deal.fundId)),
    mergeMap(deal => {
      const dealDetail = createDealDetailSelector(() => deal.dealId)(state$.value, {})
      return dealDetail ? of(NotificationsToastActions.createDealToast(dealDetail)) : EMPTY
    })
  )
}

export const dealEventEpic = combineEpics(
  dealEventReceivedEpic,
  newServicingEventReceivedEpic,
  fundAssignmentEventReceivedEpic,
  servicingEventReceivedEpic
)
